// src/components/ErrorBoundary.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Catches uncaught render errors in the screen area so a single broken screen
// does not unmount the whole React root. Resets automatically when the
// `screenKey` prop changes (e.g. adviser navigates to a different screen).
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';

interface Props {
  children: React.ReactNode;
  screenKey?: string;     // changes on navigation — used to reset after recovery
  screenLabel?: string;   // friendly name shown in the fallback card
  onBack?: () => void;    // "Go back" action (e.g. previous screen in sequence)
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface in the console for the adviser / dev; no external logging yet.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  componentDidUpdate(prevProps: Props): void {
    // Reset the error state when the user navigates to a different screen so
    // the new screen gets a clean mount rather than the fallback card.
    if (this.state.hasError && prevProps.screenKey !== this.props.screenKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { screenLabel, onBack } = this.props;
    const { error } = this.state;

    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-red-700 mb-2">
            Screen error
          </div>
          <h2 className="text-xl font-bold text-red-900 mb-2">
            Something went wrong rendering {screenLabel || 'this screen'}.
          </h2>
          <p className="text-sm text-red-800 mb-4">
            The rest of the app is still running. Use the button below to go
            back, or pick a different screen from the sidebar.
          </p>
          {error && (
            <details className="mb-4 text-xs text-red-800">
              <summary className="cursor-pointer font-semibold">Technical detail</summary>
              <pre className="mt-2 p-3 bg-white rounded border border-red-200 overflow-auto">
                {error.name}: {error.message}
                {error.stack ? '\n\n' + error.stack : ''}
              </pre>
            </details>
          )}
          {onBack && (
            <button
              onClick={onBack}
              className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
              style={{ background: '#1E3A5F', color: '#FFFFFF' }}
            >
              ← Go back
            </button>
          )}
        </div>
      </div>
    );
  }
}
